import React, {forwardRef} from 'react';
import {useSelector} from 'react-redux';
import PropTypes from 'prop-types';

import {useBreakpointValue} from '@spothero/ui';

import {useAnimationContext} from 'common/animation/AnimationContext';
import MotionBox from 'common/animation/MotionBox';
import {ROUTES} from 'constants/app-routes';
import useGenerateRoute from 'hooks/use-generate-route/useGenerateRoute';
import {useFacilityMultiSelect} from 'common/facility-sidebar/context/FacilityMultisSelectContext';
import {
    selectAllFilteredFacilities,
    selectSelectedFacility,
    selectSelectedFacilityId,
} from 'pages/facilities/store/facilities-selectors';

import NavigationMenu from './components/navigation/NavigationMenu';
import MobileNavigationMenuContainer from './MobileNavigationContainer';
import determineProductOfferings from 'utils/determine-product-offerings';

const motionVariants = {
    collapsed: {
        marginLeft: '4.375rem',
        transition: {
            type: 'spring',
            duration: 0.5,
            bounce: 0,
            delay: 0.125,
        },
    },
    expanded: {
        marginLeft: '21.875rem',
        transition: {
            type: 'spring',
            duration: 0.5,
            bounce: 0,
            delay: 0,
        },
    },
};

const menuContainerStyles = ({collapsed, isMobile}) => ({
    position: 'fixed',
    top: 0,
    left: 0,
    width: 'fill-available',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 'layer2',
    backgroundColor: 'white',
    ...(!isMobile && {
        variants: motionVariants,
        initial: {
            marginLeft: '21.875rem',
            transition: {duration: 0, bounce: 0, delay: 0},
        },
        animate: collapsed ? 'collapsed' : 'expanded',
    }),
    borderBottom: {base: 'none', tablet: '1px solid'},
    borderColor: {base: 'transparent', tablet: 'gray.medium'},
});

const TopNavHeader = forwardRef(({userData}, ref) => {
    const {
        collapsed: [collapsed],
    } = useAnimationContext();
    const isMobile = useBreakpointValue({
        base: true,
        tablet: false,
    });
    const {generateRouteWithSelectedFacilityId, generateRouteWithParams} =
        useGenerateRoute();
    const filteredFacilities = useSelector(selectAllFilteredFacilities);
    const selectedFacility = useSelector(selectSelectedFacility);
    const selectedFacilityId = useSelector(selectSelectedFacilityId);
    const isMultiSelectEnabled = useFacilityMultiSelect();
    const canViewCompPasses = useSelector(
        (state) => state.user.data.can_view_comp_passes
    );
    const {
        monthlyEnabled,
        airportEnabled,
        eventEnabled,
        airportFlexEnabled,
        compPassesEnabled,
    } = determineProductOfferings({
        filteredFacilities: isMultiSelectEnabled
            ? filteredFacilities
            : [selectedFacility],
    });

    /**
     * Array of Navigation Routes
     *
     * @type {Array<Object>}
     * @property {string} label - The label to display for the route.
     * @property {string} route - The route to navigate to.
     * @property {Array<string>} paths - The paths associated with the route. Used for matching navigation items to the current path.
     * @property {Array<Object>} [children] - An array of nested routes.
     * @property {string} children.heading - The heading for the nested routes.
     * @property {Array<Object>} children.submenu - An array of nested routes for a navigation group.
     * @property {string} children.submenu.label - The label to display for the route.
     * @property {string} children.submenu.subtitle - The subtitle to display for the route.
     * @property {string} children.submenu.route - The route to navigate to.
     * @property {Array<string>} children.submenu.paths - The paths associated with the route. Used for matching navigation items to the current path.
     */
    const NAVIGATION_ROUTES = [
        {
            label: 'Dashboard',
            route: ROUTES.dashboard,
            paths: [ROUTES.dashboard],
        },
        {
            label: 'Reservations',
            paths: [
                ROUTES.todaysReservations,
                ROUTES.reservations,
                ROUTES.compPasses,
            ],
            children: [
                {
                    heading: 'Reservations',
                    submenu: [
                        {
                            label: `Today's Reservations`,
                            subtitle: `View a real-time breakdown of today's reservations`,
                            route: generateRouteWithSelectedFacilityId(
                                ROUTES.todaysReservations
                            ),
                            paths: [ROUTES.todaysReservations],
                        },
                        {
                            label: 'All Reservations',
                            subtitle:
                                'Access all upcoming, active, and past reservation data',
                            route: ROUTES.reservations,
                            paths: [ROUTES.reservations],
                        },
                    ],
                },
                ...(canViewCompPasses
                    ? [
                          {
                              heading: 'Passes',
                              submenu: [
                                  {
                                      label: 'Complimentary Passes',
                                      subtitle:
                                          'Create and manage free parking passes',
                                      route: generateRouteWithSelectedFacilityId(
                                          ROUTES.compPasses
                                      ),
                                      paths: [ROUTES.compPasses],
                                      isDisabled:
                                          selectedFacilityId &&
                                          !compPassesEnabled,
                                  },
                              ],
                          },
                      ]
                    : []),
            ],
        },
        {
            label: 'Rates & Inventory',
            paths: [
                ROUTES.rates.standardRates,
                ROUTES.rates.monthly,
                ROUTES.rates.airport,
                ROUTES.inventory,
                ROUTES.blackouts,
                ROUTES.eventManagement,
                ROUTES.rates.comparativeSnapshot,
            ],
            children: [
                {
                    heading: 'Transient',
                    submenu: [
                        {
                            label: 'Transient Rates',
                            subtitle: 'Manage transient rates',
                            route: generateRouteWithSelectedFacilityId(
                                ROUTES.rates.transient
                            ),
                            paths: [ROUTES.rates.transient],
                        },
                        {
                            label: 'Transient Inventory',
                            subtitle: 'Manage total transient inventory',
                            route: generateRouteWithSelectedFacilityId(
                                ROUTES.inventory
                            ),
                            paths: [ROUTES.inventory],
                        },
                        {
                            label: 'Comparative Snapshot',
                            subtitle:
                                'Comparative rates based on data from local facilities',
                            route: generateRouteWithSelectedFacilityId(
                                ROUTES.rates.comparativeSnapshot
                            ),
                            paths: [ROUTES.rates.comparativeSnapshot],
                            isNew: true,
                        },
                        {
                            label: 'Blackouts',
                            subtitle:
                                'Block new sales for transient, airport, and event rates',
                            route: ROUTES.blackouts,
                            paths: [ROUTES.blackouts],
                        },
                    ],
                },
                {
                    heading: 'Events',
                    submenu: [
                        {
                            label: 'Event Pricing Defaults',
                            subtitle:
                                'Set rate and inventory rules for various types of events',
                            route: generateRouteWithSelectedFacilityId(
                                ROUTES.eventManagement,
                                {
                                    tabId: 'pricing-defaults',
                                }
                            ),
                            paths: [ROUTES.eventManagement],
                            isDisabled: selectedFacilityId && !eventEnabled,
                        },
                        {
                            label: 'Upcoming Events',
                            subtitle:
                                'Manage upcoming event rates and track sales',
                            route: generateRouteWithSelectedFacilityId(
                                ROUTES.eventManagement,
                                {
                                    tabId: 'upcoming-events',
                                }
                            ),
                            paths: [ROUTES.eventManagement],
                            isDisabled: selectedFacilityId && !eventEnabled,
                        },
                        {
                            label: 'Past Events',
                            subtitle:
                                'Review performance data from past events',
                            route: generateRouteWithSelectedFacilityId(
                                ROUTES.eventManagement,
                                {
                                    tabId: 'past-events',
                                }
                            ),
                            paths: [ROUTES.eventManagement],
                            isDisabled: selectedFacilityId && !eventEnabled,
                        },
                        {
                            label: 'Event Calendar',
                            subtitle:
                                'Easily visualize all events with a calendar view',
                            route: generateRouteWithSelectedFacilityId(
                                ROUTES.eventManagement,
                                {
                                    tabId: 'calendar',
                                }
                            ),
                            paths: [ROUTES.eventManagement],
                            isDisabled: selectedFacilityId && !eventEnabled,
                        },
                    ],
                },
                {
                    heading: 'Monthly',
                    submenu: [
                        {
                            label: 'Monthly Rates',
                            subtitle: 'View monthly rates',
                            route: generateRouteWithSelectedFacilityId(
                                ROUTES.rates.monthly
                            ),
                            paths: [ROUTES.rates.monthly],
                            isDisabled: selectedFacilityId && !monthlyEnabled,
                        },
                        {
                            label: 'Monthly Inventory',
                            subtitle:
                                'Manage total monthly inventory and track utilization',
                            route: generateRouteWithSelectedFacilityId(
                                ROUTES.inventory
                            ),
                            paths: [ROUTES.inventory],
                            isDisabled: selectedFacilityId && !monthlyEnabled,
                        },
                    ],
                },
                {
                    heading: 'Airport',
                    submenu: [
                        {
                            label: 'Airport Rates',
                            subtitle: 'Manage airport rates',
                            route: generateRouteWithSelectedFacilityId(
                                ROUTES.rates.airport,
                                {
                                    tabId: 'airport-defaults',
                                }
                            ),
                            paths: [ROUTES.rates.airport],
                            isDisabled:
                                selectedFacilityId &&
                                !(airportEnabled || airportFlexEnabled),
                        },
                        {
                            label: 'Airport Inventory',
                            subtitle:
                                'Manage total airport inventory and exceptions',
                            route: generateRouteWithSelectedFacilityId(
                                ROUTES.inventory
                            ),
                            paths: [ROUTES.inventory],
                            isDisabled:
                                selectedFacilityId &&
                                !(airportEnabled || airportFlexEnabled),
                        },
                    ],
                },
            ],
        },
        {
            label: 'Facility Information',
            paths: [
                ROUTES.facilities.facility,
                ROUTES.facilities.facilities,
                ROUTES.facilities.locationInfo,
                ROUTES.facilities.additionalAdmins,
                ROUTES.changelog,
                ROUTES.driverFeedback,
            ],
            children: [
                {
                    heading: 'Facility Management',
                    submenu: [
                        {
                            label: 'Location Information',
                            subtitle:
                                'Manage the details provided to drivers about a facility',
                            route: generateRouteWithSelectedFacilityId(
                                ROUTES.facilities.locationInfo
                            ),
                            paths: [ROUTES.facilities.locationInfo],
                        },
                        {
                            label: 'Changelog',
                            subtitle: 'Track changes made to your facilities',
                            route: ROUTES.changelog,
                            paths: [ROUTES.changelog],
                        },
                        {
                            label: 'Email Recipients',
                            subtitle:
                                'View recipients of recurring operational emails',
                            route: generateRouteWithSelectedFacilityId(
                                ROUTES.facilities.emailRecipients
                            ),
                            paths: [ROUTES.facilities.emailRecipients],
                        },
                    ],
                },
                {
                    heading: 'Driver Feedback',
                    submenu: [
                        {
                            label: 'Star Ratings & Review Tags',
                            subtitle:
                                'Explore driver-submitted ratings and review insights',
                            route: generateRouteWithSelectedFacilityId(
                                ROUTES.driverFeedback,
                                {
                                    tabId: 'star-ratings-and-review-tags',
                                }
                            ),
                            paths: [ROUTES.driverFeedback],
                        },
                        {
                            label: 'Customer Reviews',
                            subtitle:
                                'Read reviews from drivers about their parking experience',
                            route: generateRouteWithSelectedFacilityId(
                                ROUTES.driverFeedback,
                                {
                                    tabId: 'customer-reviews',
                                }
                            ),
                            paths: [ROUTES.driverFeedback],
                            isNew: true,
                        },
                    ],
                },
                {
                    heading: '', // TODO: refactor the menu to not need an empty object
                    submenu: [
                        {
                            label: '',
                            subtitle: '',
                            route: '',
                            paths: [''],
                            isDisabled: true,
                        },
                    ],
                },
            ],
        },
        {
            label: 'Accounting',
            paths: [ROUTES.accounting, ROUTES.reports],
            children: [
                {
                    heading: 'Accounting',
                    submenu: [
                        {
                            label: 'Accounting',
                            subtitle:
                                'Export remit reports for a single facility or portfolio',
                            route: ROUTES.accounting,
                            paths: [ROUTES.accounting],
                        },
                        {
                            label: 'Refunds',
                            subtitle: 'View refunded reservations',
                            route: generateRouteWithParams(ROUTES.reports, {
                                tabId: 'refunds',
                            }),
                            paths: [ROUTES.reports],
                        },
                    ],
                },
            ],
        },
    ];

    return (
        <MotionBox
            as="header"
            role="banner"
            data-testid={`top-nav-header-${isMobile ? 'mobile' : 'desktop'}`}
            {...menuContainerStyles({collapsed, isMobile})}
        >
            {!isMobile ? (
                <NavigationMenu
                    menuRef={ref}
                    navigationRoutes={NAVIGATION_ROUTES}
                />
            ) : (
                <MobileNavigationMenuContainer
                    menuRef={ref}
                    userEmail={userData?.email}
                    navigationRoutes={NAVIGATION_ROUTES}
                />
            )}
        </MotionBox>
    );
});

TopNavHeader.propTypes = {
    userData: PropTypes.object,
};

export default TopNavHeader;
